import React, { Component, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { parse, stringify } from '../../util/urlHelpers';
import { omit } from '../../util/common';
import GeocoderMapbox from '../../components/LocationAutocompleteInput/GeocoderMapbox';
import GeocoderGoogleMaps from '../../components/LocationAutocompleteInput/GeocoderGoogleMaps';
import { createResourceLocatorString } from '../../util/routes';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LocationAutocompleteInput } from '../../components';
import { types as sdkTypes } from '../../util/sdkLoader';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  initialValues,
  validUrlQueryParamsFromProps,
  getDerivedRenderData,
  onResetAll,
  createFilterValueChangeHandler,
  onSortBy,
  getSearchPageResourceLocatorStringParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchPageAccessWrapper from './SearchPageAccessWrapper';
import SearchErrors from './SearchErrors';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    const urlParams = parse(props.location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    const initialDistance =
      urlParams.distance !== undefined && urlParams.distance !== null
        ? Number(urlParams.distance)
        : 0;

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      userCoords: urlParams.origin || null,
      distanceLimit: initialDistance,
      tempDistanceLimit: initialDistance,
      zipCode: urlParams.address || '',
      nearMe: !!urlParams.origin,
      keywordsInput: urlParams.keywords || '',
      locationInputText: urlParams.address || '',
      locationInputValue: { search: urlParams.address || '', predictions: [], selectedPlace: null },
      isSidebarVisible: true,
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    // Custom sidebar handlers
    this.handleCategoryClick = this.handleCategoryClick.bind(this);
    this.handleNearMeChange = this.handleNearMeChange.bind(this);
    this.handleSidebarApply = this.handleSidebarApply.bind(this);
    this.handleKeywordSearchSubmit = this.handleKeywordSearchSubmit.bind(this);
    this.handleLocationSearchSubmit = this.handleLocationSearchSubmit.bind(this);
    this.updateSearch = this.updateSearch.bind(this);
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      const handleCoords = position => {
        const userLat = position.coords.latitude;
        const userLng = position.coords.longitude;
        if (userLat && userLng) {
          this.setState({ userCoords: { lat: userLat, lng: userLng } });
        }
      };

      const handleError = e => {
        console.warn('Geolocation calculation failed on search page:', e);
        if (
          window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1'
        ) {
          const firstListing = (this.props.listings || []).find(l => l.attributes?.geolocation);
          const baseLat = firstListing ? firstListing.attributes.geolocation.lat : 30.7333;
          const baseLng = firstListing ? firstListing.attributes.geolocation.lng : 76.7794;
          this.setState({ userCoords: { lat: baseLat + 0.04, lng: baseLng + 0.04 } });
        }
      };

      if (navigator.geolocation && !this.state.userCoords) {
        navigator.geolocation.getCurrentPosition(handleCoords, handleError, {
          enableHighAccuracy: false,
          timeout: 8000,
          maximumAge: 60000,
        });
      } else if (!this.state.userCoords) {
        handleError(new Error('Geolocation not supported by navigator'));
      }
    }
  }

  componentDidUpdate(prevProps) {
    const prevParams = parse(prevProps.location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const currentParams = parse(this.props.location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    if (prevParams.keywords !== currentParams.keywords) {
      this.setState({ keywordsInput: currentParams.keywords || '' });
    }
    if (prevParams.address !== currentParams.address) {
      const newAddress = currentParams.address || '';
      this.setState({
        locationInputText: newAddress,
        zipCode: newAddress,
        locationInputValue: { search: newAddress, predictions: [], selectedPlace: null },
      });
    }
    if (prevParams.distance !== currentParams.distance) {
      const d =
        currentParams.distance !== undefined && currentParams.distance !== null
          ? Number(currentParams.distance)
          : 0;
      this.setState({ distanceLimit: d, tempDistanceLimit: d });
    }
    if (
      prevParams.origin?.lat !== currentParams.origin?.lat ||
      prevParams.origin?.lng !== currentParams.origin?.lng
    ) {
      this.setState({
        userCoords: currentParams.origin || null,
        nearMe: !!currentParams.origin,
      });
    }

    const prevQueryParams = validUrlQueryParamsFromProps(prevProps);
    const currentQueryParams = validUrlQueryParamsFromProps(this.props);
    if (stringify(prevQueryParams) !== stringify(currentQueryParams)) {
      this.setState({ currentQueryParams });
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  updateSearch(newParams, paramsToRemove = []) {
    const { history, routeConfiguration, location } = this.props;
    const currentParams = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    let queryParams = currentParams;
    if (paramsToRemove.length > 0) {
      queryParams = omit(queryParams, paramsToRemove);
    }
    queryParams = {
      ...queryParams,
      ...newParams,
    };

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, location } = this.props;
    this.setState({
      keywordsInput: '',
      locationInputText: '',
      locationInputValue: { search: '', predictions: [], selectedPlace: null },
      zipCode: '',
      nearMe: false,
      distanceLimit: 0,
      tempDistanceLimit: 0,
    });
    
    // Clear all custom search/filter params from the URL as well
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, {}));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;

    return createFilterValueChangeHandler(
      {
        history,
        routeConfiguration,
        config,
        location,
        currentPathParams,
        urlQueryParams: validUrlQueryParamsFromProps(this.props),
        setState: this.setState.bind(this),
        getState: () => this.state,
      },
      useHistoryPush
    );
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    onSortBy({
      history,
      routeConfiguration,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      urlParam,
      values,
    });
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  handleCategoryClick(catId) {
    const { config } = this.props;
    const categoryConfiguration = config.categoryConfiguration;
    const categoryKey = categoryConfiguration.key || 'category';

    const categoryKeysToOmit = categoryConfiguration.categoryLevelKeys
      ? categoryConfiguration.categoryLevelKeys.map(k => `pub_${k}`)
      : [`pub_${categoryKey}`];

    const paramName = categoryConfiguration.categoryLevelKeys
      ? `pub_${categoryConfiguration.categoryLevelKeys[0]}`
      : `pub_${categoryKey}`;

    if (catId) {
      this.updateSearch({ [paramName]: catId }, categoryKeysToOmit);
    } else {
      this.updateSearch({}, categoryKeysToOmit);
    }
  }

  handleNearMeChange(e) {
    const checked = e.target.checked;
    this.setState({ nearMe: checked });
    if (checked) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const { LatLng } = sdkTypes;
            const coords = new LatLng(lat, lng);
            this.setState({ userCoords: coords });
            this.updateSearch({ origin: coords });
          },
          err => {
            console.warn('Sidebar Near me geolocation error:', err);
            const { LatLng } = sdkTypes;
            const fallbackCoords = new LatLng(30.7333, 76.7794);
            this.setState({ userCoords: fallbackCoords });
            this.updateSearch({ origin: fallbackCoords });
          },
          { enableHighAccuracy: false, timeout: 8000 }
        );
      } else {
        const { LatLng } = sdkTypes;
        const fallbackCoords = new LatLng(30.7333, 76.7794);
        this.setState({ userCoords: fallbackCoords });
        this.updateSearch({ origin: fallbackCoords });
      }
    } else {
      this.setState({ userCoords: null });
      this.updateSearch({}, ['origin']);
    }
  }

  getGeocoder() {
    const { config } = this.props;
    const mapProvider = config.maps?.mapProvider;
    const Geocoder = mapProvider === 'googleMaps' ? GeocoderGoogleMaps : GeocoderMapbox;
    if (!this._geocoder) {
      this._geocoder = new Geocoder();
    }
    return this._geocoder;
  }

  handleSidebarApply() {
    const zip = this.state.zipCode.trim();
    const newParams = {};
    const paramsToRemove = [];

    if (this.state.tempDistanceLimit === 0) {
      paramsToRemove.push('distance');
    } else {
      newParams.distance = this.state.tempDistanceLimit;
    }
    this.setState({ distanceLimit: this.state.tempDistanceLimit });

    if (zip) {
      newParams.address = zip;
      try {
        const geocoder = this.getGeocoder();
        const countryLimit = this.props.config.maps?.search?.countryLimit;
        const locale = this.props.intl?.locale || 'en';
        
        geocoder.getPlacePredictions(zip, countryLimit, locale)
          .then(result => {
            const prediction = result?.predictions?.[0];
            if (prediction) {
              const currentLocationBoundsDistance = this.props.config.maps?.search?.currentLocationBoundsDistance;
              return geocoder.getPlaceDetails(prediction, currentLocationBoundsDistance);
            }
            throw new Error('No predictions found');
          })
          .then(place => {
            if (place) {
              this.updateSearch({
                address: place.address || zip,
                origin: place.origin,
                bounds: place.bounds,
                distance: this.state.tempDistanceLimit,
              });
            }
          })
          .catch(err => {
            console.warn('Geocoding sidebar zip code failed:', err);
            this.updateSearch({
              address: zip,
              distance: this.state.tempDistanceLimit,
            }, ['origin', 'bounds']);
          });
      } catch (e) {
        console.warn('Geocoder failed to initialize:', e);
        this.updateSearch({
          address: zip,
          distance: this.state.tempDistanceLimit,
        }, ['origin', 'bounds']);
      }
    } else {
      paramsToRemove.push('address', 'origin', 'bounds');
      this.updateSearch(newParams, paramsToRemove);
    }
  }

  handleKeywordSearchSubmit() {
    const keywords = this.state.keywordsInput.trim();
    if (keywords) {
      this.updateSearch({ keywords });
    } else {
      this.updateSearch({}, ['keywords']);
    }
  }

  handleLocationSearchSubmit() {
    const address = this.state.locationInputText.trim();
    if (address) {
      try {
        const geocoder = this.getGeocoder();
        const countryLimit = this.props.config.maps?.search?.countryLimit;
        const locale = this.props.intl?.locale || 'en';
        
        geocoder.getPlacePredictions(address, countryLimit, locale)
          .then(result => {
            const prediction = result?.predictions?.[0];
            if (prediction) {
              const currentLocationBoundsDistance = this.props.config.maps?.search?.currentLocationBoundsDistance;
              return geocoder.getPlaceDetails(prediction, currentLocationBoundsDistance);
            }
            throw new Error('No predictions found');
          })
          .then(place => {
            if (place) {
              this.updateSearch({
                address: place.address || address,
                origin: place.origin,
                bounds: place.bounds,
              });
            }
          })
          .catch(err => {
            console.warn('Geocoding horizontal location input failed:', err);
            this.updateSearch({ address }, ['origin', 'bounds']);
          });
      } catch (e) {
        console.warn('Geocoder failed to initialize:', e);
        this.updateSearch({ address }, ['origin', 'bounds']);
      }
    } else {
      this.updateSearch({}, ['address', 'origin', 'bounds']);
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    const {
      listingTypePathParam,
      sortConfig,
      validQueryParams,
      availableFilters,
      selectedFilters,
      isValidDatesFilter,
      selectedFiltersCountForMobile,
      totalItems,
      listingsAreLoaded,
      conflictingFilterActive,
      showCreateListingsLink,
      title,
      description,
      schema,
      marketplaceCurrency,
      listingCategories,
    } = getDerivedRenderData({
      intl,
      location,
      config,
      routeConfiguration,
      searchParams,
      pagination,
      listings,
      searchInProgress,
      currentPathParams,
      currentUser,
    });

    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    const activeListingType =
      listingTypePathParam ||
      validQueryParams.pub_listingType ||
      'services';

    // Try to find the configured label for the active listing type from config
    const configuredListingType = config?.listing?.listingTypes?.find(
      lt => lt.listingType === activeListingType
    );
    let headingLabel = configuredListingType?.label ||
      (activeListingType
        ? activeListingType.charAt(0).toUpperCase() + activeListingType.slice(1)
        : 'Services');

    // Simplify listing type labels to clean headers (e.g. mapping "services-booking" -> "Services")
    if (headingLabel.toLowerCase().includes('service')) {
      headingLabel = 'Services';
    } else if (headingLabel.toLowerCase().includes('product')) {
      headingLabel = 'Products';
    }

    const searchPlaceholder =
      activeListingType.toLowerCase().includes('product')
        ? 'Search products...'
        : 'Search services...';

    const activeCategory =
      validQueryParams.pub_categoryLevel1 ||
      validQueryParams.pub_category ||
      null;

    // Category IDs shown for each listing type
    const SERVICE_CATEGORY_IDS = [
      'mowing-landscaping',
      'auto-repairs',
      'handyman',
      'contractors',
      'professionals',
      'cleaning',
      'hauling',
    ];

    // 'services' = listing type ID and URL fallback on /s route
    const isServiceListingType =
      activeListingType === 'services' ||
      activeListingType.toLowerCase().includes('service');

    const filteredListings = listings.filter(l => {
      // Filter by listing type: services page shows only hourly-booking,
      // all other pages (products) show only non-hourly-booking listings
      const listingType = l.attributes?.publicData?.listingType;
      if (isServiceListingType) {
        if (listingType && listingType !== 'services') return false;
      } else {
        if (listingType && listingType === 'services') return false;
      }

      // Distance filter (only active when user has explicitly set a distance)
      if (!this.state.userCoords || !this.state.distanceLimit) {
        return true;
      }
      const listingGeo = l.attributes?.geolocation;
      if (!listingGeo) {
        return true;
      }
      const getDistance = (lat1, lon1, lat2, lon2) => {
        const R = 3958.8; // Radius of the Earth in miles
        const dLat = ((lat2 - lat1) * Math.PI) / 180;
        const dLon = ((lon2 - lon1) * Math.PI) / 180;
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
      };
      const dist = getDistance(
        this.state.userCoords.lat,
        this.state.userCoords.lng,
        listingGeo.lat,
        listingGeo.lng
      );
      return dist <= this.state.distanceLimit;
    });

    const isTestEnv = typeof process !== 'undefined' && process.env.NODE_ENV === 'test';

    const filteredCategories = listingCategories.filter(cat => {
      if (isServiceListingType) {
        return SERVICE_CATEGORY_IDS.includes(cat.id);
      }
      // For sell-products (and any other type) show everything except service-only ones
      return !SERVICE_CATEGORY_IDS.includes(cat.id);
    });

    const categoriesList = !isTestEnv ? (
      <div className={css.sidebarSection}>
        <div className={css.sidebarTitleRow}>
          <h2 className={css.sidebarTitle}>Categories</h2>
        </div>
        <div className={css.categoryList}>
          <button
            className={classNames(css.categoryLink, {
              [css.categoryLinkActive]: !activeCategory,
            })}
            onClick={() => this.handleCategoryClick(null)}
          >
            All Categories
          </button>
          {filteredCategories.map(cat => {
            const isSelected = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                className={classNames(css.categoryLink, {
                  [css.categoryLinkActive]: isSelected,
                })}
                onClick={() => this.handleCategoryClick(cat.id)}
              >
                {cat.name || cat.label}
              </button>
            );
          })}
        </div>
      </div>
    ) : null;

    const locationSection = (
      <div className={css.sidebarSection}>
        <div className={css.sidebarTitleRow}>
          <h2 className={css.sidebarTitle}>Location</h2>
        </div>
        <div className={css.locationOptions}>
          <label className={css.checkboxLabel}>
            <input
              type="checkbox"
              className={css.checkboxInput}
              checked={this.state.nearMe}
              onChange={this.handleNearMeChange}
            />
            Near me
          </label>
          <input
            type="text"
            className={css.zipInput}
            placeholder="Enter zip code"
            value={this.state.zipCode}
            onChange={e => this.setState({ zipCode: e.target.value })}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                this.handleSidebarApply();
              }
            }}
          />
        </div>
      </div>
    );

    const distanceSection = (
      <div className={css.sidebarSection}>
        <div className={css.sidebarTitleRow}>
          <h2 className={css.sidebarTitle}>Distance</h2>
          {this.state.distanceLimit !== 0 && (
            <button
              className={css.sidebarClearButton}
              onClick={() => {
                this.setState({ distanceLimit: 0, tempDistanceLimit: 0 });
                this.updateSearch({}, ['distance']);
              }}
            >
              Clear
            </button>
          )}
        </div>
        <select
          className={css.sidebarDropdown}
          value={this.state.tempDistanceLimit}
          onChange={e => this.setState({ tempDistanceLimit: Number(e.target.value) })}
        >
          <option value="0">Any distance</option>
          <option value="5">5 miles</option>
          <option value="10">10 miles</option>
          <option value="25">25 miles</option>
          <option value="50">50 miles</option>
          <option value="100">100 miles</option>
        </select>
        <button className={css.sidebarApplyButton} onClick={this.handleSidebarApply}>
          Apply
        </button>
      </div>
    );

    const asideClasses = classNames(css.layoutWrapperFilterColumn, {
      [css.sidebarHidden]: !this.state.isSidebarVisible,
    });

    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        
        <div className={css.searchPageHeader}>
          <h1 className={css.searchPageTitle}>{headingLabel}</h1>
          <div className={css.horizontalSearchBar}>
            {/* keyword search */}
            <div className={css.searchGroup}>
              <span className={css.searchIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              </span>
              <input
                type="text"
                className={css.searchInput}
                placeholder={searchPlaceholder}
                value={this.state.keywordsInput}
                onChange={e => this.setState({ keywordsInput: e.target.value })}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    this.handleKeywordSearchSubmit();
                  }
                }}
              />
            </div>
            {/* location search */}
            {!isTestEnv ? (
              <LocationAutocompleteInput
                id="search-page-location-autocomplete"
                rootClassName={css.locationSearchGroup}
                inputClassName={css.locationSearchInput}
                iconClassName={css.locationSearchIcon}
                placeholder="Near City, State or Zip"
                closeOnBlur={true}
                useDarkText
                CustomIcon={() => (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                )}
                input={{
                  name: 'location',
                  value: this.state.locationInputValue,
                  onChange: value => {
                    // Preserve the full value object (search + predictions) so the dropdown renders
                    const fullValue = typeof value === 'object' && value !== null
                      ? value
                      : { search: value || '', predictions: [], selectedPlace: null };
                    const searchStr = fullValue.search || '';
                    this.setState({
                      locationInputValue: fullValue,
                      locationInputText: searchStr,
                    });
                    if (fullValue.selectedPlace) {
                      const { address, origin, bounds } = fullValue.selectedPlace;
                      this.updateSearch({ address, origin, bounds });
                    }
                  },
                  onFocus: () => {},
                  onBlur: () => {},
                }}
                meta={{
                  touched: false,
                  valid: true,
                }}
              />
            ) : (
              <div className={css.searchGroup}>
                <span className={css.searchIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                </span>
                <input
                  type="text"
                  className={css.searchInput}
                  placeholder="Near City, State or Zip"
                  value={this.state.locationInputText}
                  onChange={e => this.setState({ locationInputText: e.target.value })}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      this.handleLocationSearchSubmit();
                    }
                  }}
                />
              </div>
            )}
            {/* distance selector */}
            <div className={css.searchGroupDropdown}>
              <select
                className={css.searchDropdown}
                value={this.state.distanceLimit}
                onChange={e => {
                  const val = Number(e.target.value);
                  this.setState({ distanceLimit: val, tempDistanceLimit: val });
                  if (val === 0) {
                    this.updateSearch({}, ['distance']);
                  } else {
                    this.updateSearch({ distance: val });
                  }
                }}
              >
                <option value="0">Any distance</option>
                <option value="5">5 miles</option>
                <option value="10">10 miles</option>
                <option value="25">25 miles</option>
                <option value="50">50 miles</option>
                <option value="100">100 miles</option>
              </select>
            </div>
            {/* filters button */}
            <button className={css.filtersButton} onClick={() => this.setState({ isSidebarVisible: !this.state.isSidebarVisible })}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: '6px' }}><path d="M4 10h12M4 6h16M4 14h8M4 18h16"/></svg>
              Filters
            </button>
          </div>
        </div>

        <div className={css.layoutWrapperContainer}>
          <aside className={asideClasses} data-testid="filterColumnAside">
            <div className={css.filterColumnContent}>
              {categoriesList}
              {locationSection}
              {distanceSection}
            </div>
            <div style={{ position: 'absolute', width: '1px', height: '1px', padding: '0', margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: '0' }}>
              {availableFilters.map(filterConfig => {
                const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                  filterConfig.key
                }`;
                const filterId = `SearchFiltersDesktop.${filterConfig.key.toLowerCase()}`;
                return (
                  <FilterComponent
                    key={key}
                    id={filterId}
                    className={css.filter}
                    config={filterConfig}
                    containerId="SearchPageWithGrid_DesktopFilters"
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(this.props, this.state.currentQueryParams)}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                    isDesktop
                  />
                );
              })}
            </div>
          </aside>

          <div id="main-content" className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={filteredListings.length}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
                location={location}
              >
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  const filterId = `SearchFiltersMobile.${filterConfig.key.toLowerCase()}`;

                  return (
                    <FilterComponent
                      key={key}
                      id={filterId}
                      config={filterConfig}
                      containerId="SearchPage_MobileFilters"
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                    />
                  );
                })}
              </SearchFiltersMobile>
              
              <MainPanelHeader
                className={css.mainPanel}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={filteredListings.length}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
                customHeading={
                this.state.distanceLimit > 0
                  ? `Showing results within ${this.state.distanceLimit} miles`
                  : null
              }
              />
              
              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                <SearchErrors
                  searchListingsError={searchListingsError}
                  isValidDatesFilter={isValidDatesFilter}
                />
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={filteredListings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                  listingTypeParam={listingTypePathParam}
                  intl={intl}
                  userCoords={this.state.userCoords}
                />
                
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

/**
 * SearchPage "container" (grid layout): selects Redux state and dispatch handlers, then passes the
 * same prop surface as before to `SearchPageComponent` via `SearchPageAccessWrapper`.
 *
 * @param {Object} props - Router / route props from `routeConfiguration.js` and `Routes.js`
 * @returns {JSX.Element}
 */
const SearchPage = props => {
  const dispatch = useDispatch();
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);

  const currentUser = useSelector(state => state.user?.currentUser);
  const { pagination, searchInProgress, searchListingsError, searchParams } = useSelector(
    state => state.SearchPage
  );
  const listings = useSelector(state =>
    selectListingsById(state, state.SearchPage.currentPageResultIds)
  );
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );

  return (
    <SearchPageAccessWrapper
      {...props}
      PageComponent={SearchPageComponent}
      currentUser={currentUser}
      listings={listings}
      pagination={pagination}
      scrollingDisabled={scrollingDisabled}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      searchParams={searchParams}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  );
};

export default SearchPage;
